import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Form, Button, Spinner, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useMutation } from '@apollo/client'
import {
    useChannelConfig,
    ChannelConfigData,
    VicidialCampaignOption,
    fetchVicidialCampaignOptions,
} from '../hooks/useChannelConfig'
import { useOrgChannels } from '../hooks/useOrgChannels'
import { useChannelValidation } from '../hooks/useChannelValidation'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { ChannelFormModal } from '../../assignmentGroups/components/ChannelFormModal'
import { Channel } from '../../../types/assignmentGroups'
import { DifySection } from './DifySection'
import { AiAssistantSection } from './AiAssistantSection'
import { PromptConfigSection } from './PromptConfigSection'
import { UPDATE_AI_CONFIG, UPDATE_PROMPT_CONFIG } from '../../../gql/mutations/channelMutations'
import { URL as BACKEND_URL } from '../../../gql/client'
import { FormattedMessage, useIntl } from 'react-intl'
import { TELEPHONY_MESSAGES as TM } from '../../../helpers/telephonyMessages'
import {
    CHANNEL_CONFIG_MESSAGES as M,
    EM_DASH,
    LABELS,
    WHATSAPP_PROVIDER_OPTIONS,
    campaignOptionLabel,
} from '../channelConfigMessages'

// Las URLs de webhook son datos, no copy, pero como template literal dentro del
// JSX el rule las marca igual. Botmaker apunta a app.policytools.com y no a
// BACKEND_URL: se conserva para no cambiar la URL que los clientes registraron.
const WEBHOOK_URL = {
    twilioSms: (token: string) => `${BACKEND_URL}/webhooks/twilio/sms/${token}/`,
    twilioWhatsapp: (token: string) => `${BACKEND_URL}/webhooks/twilio/whatsapp/${token}/`,
    twilioStatus: () => `${BACKEND_URL}/webhooks/twilio/status/`,
    meta: (token: string) => `${BACKEND_URL}/webhooks/meta/${token}/`,
    botmaker: (token: string) => `https://app.policytools.com/webhooks/botmaker/${token}/`,
}

interface OrganizationChannelConfigPanelSections {
    telephony?: boolean
    messaging?: boolean
    aiAssistant?: boolean
    email?: boolean
    integrations?: boolean
}

interface OrganizationChannelConfigPanelProps {
    // Already-decoded numeric-string organization id (NOT a relay-encoded id).
    // Optional: when omitted, the underlying scoped config resolves to the
    // current user's own organization server-side (mirrors useChannelConfig(undefined)).
    organizationId?: string
    // Which sections to render. Omit to show everything (e.g. the standalone
    // /channel-config page). Callers that embed this panel in a scoped tab
    // (e.g. OneOrganizationPage) pass only the subset relevant to that tab.
    sections?: OrganizationChannelConfigPanelSections
}

const ALL_SECTIONS_VISIBLE: Required<OrganizationChannelConfigPanelSections> = {
    telephony: true,
    messaging: true,
    aiAssistant: true,
    email: true,
    integrations: true,
}

export const OrganizationChannelConfigPanel = ({ organizationId, sections }: OrganizationChannelConfigPanelProps) => {
    const intl = useIntl()
    const visible = sections ?? ALL_SECTIONS_VISIBLE
    const { auth } = useAuth()

    const { config, loading, saving, saveOrgConfig } = useChannelConfig(organizationId)
    const { saving: channelsSaving, saveChannel } = useOrgChannels(organizationId)
    const [updateAiConfigGql] = useMutation(UPDATE_AI_CONFIG)
    const [updatePromptConfigGql] = useMutation(UPDATE_PROMPT_CONFIG)

    const [form, setForm] = useState<Partial<ChannelConfigData>>({})
    const [isDirty, setIsDirty] = useState(false)

    // Conversational AI depends on a messaging channel actually being configured —
    // it only makes sense to auto-reply on a channel that exists.
    const hasMessagingChannel = Boolean(form.twilio_enabled) || (!!form.whatsapp_provider && form.whatsapp_provider !== 'NONE')
    const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
    const [loadingCampaigns, setLoadingCampaigns] = useState(false)

    // Channel modal state
    const [channelModalOpen, setChannelModalOpen] = useState(false)
    const [editingChannel, setEditingChannel] = useState<Channel | null>(null)

    // Validation hooks
    const {
        validateTwilioSms,
        validateTwilioWhatsapp,
        validateBotmakerWhatsapp,
        validateMetaWhatsapp,
    } = useChannelValidation()

    // Validation state
    const [validatingTwilioSms, setValidatingTwilioSms] = useState(false)
    const [validatingWhatsapp, setValidatingWhatsapp] = useState(false)

    // Token visibility toggles
    const [showTwilioToken, setShowTwilioToken] = useState(false)
    const [showBotmakerToken, setShowBotmakerToken] = useState(false)
    const [showTwilioWaToken, setShowTwilioWaToken] = useState(false)
    const [showMetaToken, setShowMetaToken] = useState(false)

    // Sync form when config loads or org changes
    useEffect(() => {
        if (!loading) {
            setForm(config)
            setIsDirty(false)
            setCampaignOptions([])
        }
    }, [config, loading])

    const getToken = useCallback(() => {
        return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
    }, [auth?.api_token])

    const visibleCampaignOptions = useMemo(() => {
        const currentCampaignId = String(form.vicidial_campaign_id || '').trim()
        if (!currentCampaignId || campaignOptions.some((campaign) => campaign.campaign_id === currentCampaignId)) {
            return campaignOptions
        }

        return [
            {
                campaign_id: currentCampaignId,
                campaign_name: 'Current organization default',
                active: 'Y',
            },
            ...campaignOptions,
        ]
    }, [campaignOptions, form.vicidial_campaign_id])

    const handleChange = (field: keyof ChannelConfigData, value: any) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setIsDirty(true)
    }

    const handleLoadCampaigns = async () => {
        const token = getToken()
        if (!token) {
            toast.error(intl.formatMessage(M.toastLoginRequiredCampaigns))
            return
        }

        const baseUrl = String(form.vicidial_base_url || '').trim()
        const apiUser = String(form.vicidial_api_user || '').trim()
        const apiPassword = String(form.vicidial_api_password || '').trim()

        if (!baseUrl || !apiUser || !apiPassword) {
            toast.error(intl.formatMessage(M.toastCampaignCredentialsRequired))
            return
        }

        setLoadingCampaigns(true)
        try {
            const campaigns = await fetchVicidialCampaignOptions({
                token,
                baseUrl,
                apiUser,
                apiPassword,
            })
            setCampaignOptions(campaigns)

            if (!campaigns.length) {
                toast.info(intl.formatMessage(M.toastNoCampaignsReturned))
                return
            }

            toast.success(intl.formatMessage(M.toastCampaignsLoaded))
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Unable to load PolicyDial campaigns.'
            toast.error(message)
            setCampaignOptions([])
        } finally {
            setLoadingCampaigns(false)
        }
    }

    const handleSave = async () => {
        // Save AI config (conversational engine + AI Assistant) via GraphQL mutation
        try {
            await updateAiConfigGql({
                variables: {
                    scope: 'organization',
                    targetId: organizationId ? parseInt(organizationId, 10) : undefined,
                    difyEnabled: Boolean(form.dify_enabled),
                    difyApiKey: form.dify_api_key || '',
                    difyApiUrl: form.dify_api_url || 'https://api.dify.ai/v1',
                    difyAppId: form.dify_app_id || '',
                    aiProvider: form.ai_provider || 'DIFY',
                    llmProvider: form.llm_provider || 'ANTHROPIC',
                    llmApiKey: form.llm_api_key || '',
                    llmModel: form.llm_model || '',
                    aiAssistantEnabled: form.ai_assistant_enabled,
                    aiAssistantDomains: form.ai_assistant_domains || {},
                },
            })
        } catch (gqlErr) {
            console.error('Error saving AI config via GraphQL:', gqlErr)
        }

        try {
            await updatePromptConfigGql({
                variables: {
                    scope: 'organization',
                    targetId: organizationId ? parseInt(organizationId, 10) : undefined,
                    promptHeader: form.prompt_header || '',
                    promptBody: form.prompt_body || '',
                    promptFooter: form.prompt_footer || '',
                    shareContactInfo: form.share_contact_info,
                    shareQuotes: form.share_quotes,
                    sharePolicies: form.share_policies,
                    shareLeads: form.share_leads,
                    shareConsentStatus: form.share_consent_status,
                },
            })
        } catch (gqlErr) {
            console.error('Error saving Prompt config via GraphQL:', gqlErr)
        }

        const result = await saveOrgConfig(form)
        if (result.ok) {
            toast.success(intl.formatMessage(M.toastConfigSaved))
            setIsDirty(false)
        } else {
            toast.error(intl.formatMessage(M.toastConfigSaveError, {message: result.error}), { autoClose: 8000 })
        }
    }

    const handleSaveChannel = async (data: Partial<Channel>) => {
        try {
            const saved = await saveChannel(data)
            if (saved) {
                toast.success(intl.formatMessage(data.id ? M.toastChannelUpdated : M.toastChannelCreated))
                setChannelModalOpen(false)
                setEditingChannel(null)
            }
            return saved as any
        } catch (error: any) {
            const errorMessage = error?.response?.data?.error || 'Error saving channel'
            toast.error(errorMessage)
            throw error
        }
    }

    const handleValidateTwilioSms = async () => {
        const accountSid = form.twilio_account_sid?.trim()
        const authToken = form.twilio_auth_token?.trim()

        if (!accountSid || !authToken) {
            toast.warning(intl.formatMessage(M.toastFillAccountSid))
            return
        }

        setValidatingTwilioSms(true)
        try {
            const result = await validateTwilioSms({
                variables: { accountSid, authToken },
            })
            if (result.data?.validateTwilioSms?.payload?.valid) {
                toast.success(result.data.validateTwilioSms.payload.message)
            } else {
                toast.error(result.data?.validateTwilioSms?.payload?.message || intl.formatMessage(M.toastValidationFailed))
            }
        } catch (error: any) {
            toast.error(error.message || intl.formatMessage(M.toastValidationError))
        } finally {
            setValidatingTwilioSms(false)
        }
    }

    const handleValidateWhatsapp = async () => {
        const provider = form.whatsapp_provider?.trim()

        if (!provider || provider === 'NONE') {
            toast.warning(intl.formatMessage(M.toastSelectWhatsAppProvider))
            return
        }

        setValidatingWhatsapp(true)
        try {
            let result
            if (provider === 'TWILIO') {
                const accountSid = form.twilio_whatsapp_account_sid?.trim() || form.twilio_account_sid?.trim()
                const authToken = form.twilio_whatsapp_auth_token?.trim() || form.twilio_auth_token?.trim()

                if (!accountSid || !authToken) {
                    toast.warning(intl.formatMessage(M.toastFillTwilioCredentials))
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateTwilioWhatsapp({
                    variables: { accountSid, authToken },
                })
            } else if (provider === 'BOTMAKER') {
                const apiToken = form.botmaker_api_token?.trim()
                const defaultChannelId = form.botmaker_default_channel_id?.trim()
                const apiUrl = form.botmaker_api_url?.trim() || undefined

                if (!apiToken || !defaultChannelId) {
                    toast.warning(intl.formatMessage(M.toastFillApiTokenChannel))
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateBotmakerWhatsapp({
                    variables: { apiToken, defaultChannelId, apiUrl },
                })
            } else if (provider === 'META') {
                const accessToken = form.meta_whatsapp_access_token?.trim()
                const phoneNumberId = form.meta_whatsapp_phone_number_id?.trim()

                if (!accessToken || !phoneNumberId) {
                    toast.warning(intl.formatMessage(M.toastFillAccessTokenPhoneId))
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateMetaWhatsapp({
                    variables: { accessToken, phoneNumberId },
                })
            }

            if (result?.data) {
                const payload = (Object.values(result.data)[0] as any)?.payload
                if (payload?.valid) {
                    toast.success(payload.message)
                } else {
                    toast.error(payload?.message || intl.formatMessage(M.toastValidationFailed))
                }
            }
        } catch (error: any) {
            toast.error(error.message || intl.formatMessage(M.toastValidationError))
        } finally {
            setValidatingWhatsapp(false)
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" variant="primary" />
            </div>
        )
    }

    return (
        <div>
            {/* Header row: webhook token badge + Save Changes button */}
            <div className="d-flex justify-content-end align-items-center gap-3 mb-6">
                {config.webhook_token && (
                    <span className="text-muted fs-7">
                        <FormattedMessage
                            {...M.pageWebhookToken}
                            values={{
                                token: `${config.webhook_token.slice(0, 12)}...`,
                                code: (chunks) => <code className="text-primary">{chunks}</code>,
                            }}
                        />
                    </span>
                )}
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || !isDirty}
                    className="px-6"
                >
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {intl.formatMessage(M.saving)}
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-lg me-1"></i>
                            {intl.formatMessage(M.saveChanges)}
                        </>
                    )}
                </Button>
            </div>

            {/* ═════ TELEPHONY SYSTEM ═════ */}
            {visible.telephony && (
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-telephone-forward fs-2 text-primary"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">{intl.formatMessage(M.sectionTelephony)}</h4>
                    </div>

                    {/* ─── Provider Toggle ─── */}
                    <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded bg-light border">
                        <div className="fw-semibold text-gray-700 flex-shrink-0">{LABELS.ringCentral}</div>
                        <Form.Check
                            type="switch"
                            id="provider_toggle"
                            label={
                                <span className="d-inline-flex align-items-center gap-1">
                                    <img
                                        src="/media/tfi/policydial-icon.svg"
                                        alt={LABELS.policyDial}
                                        style={{ width: 18, height: 18, objectFit: 'contain' }}
                                    />
                                    {LABELS.policyDial}
                                </span>
                            }
                            checked={form.telephony_provider === 'VICIDIAL'}
                            onChange={(e) => handleChange('telephony_provider', e.target.checked ? 'VICIDIAL' : 'RINGCENTRAL')}
                            className="fw-semibold"
                        />
                    </div>

                    {/* ─── PolicyDial Configuration ─── */}
                    {form.telephony_provider === 'VICIDIAL' && (
                        <div className="border rounded p-4 bg-light">
                            <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                                <img
                                    src="/media/tfi/policydial-webphone.svg"
                                    alt={LABELS.policyDial}
                                    style={{ height: '32px', objectFit: 'contain' }}
                                />
                                <h5 className="fw-bold text-gray-800 mb-0">{intl.formatMessage(M.configuration)}</h5>
                                <div className="ms-auto d-flex align-items-center gap-2">
                                    <Form.Check
                                        type="switch"
                                        id="vicidial_enabled"
                                        checked={form.vicidial_enabled || false}
                                        onChange={(e) => handleChange('vicidial_enabled', e.target.checked)}
                                        className="mb-0"
                                    />
                                    <Badge bg={form.vicidial_enabled ? 'success' : 'secondary'}>
                                        {intl.formatMessage(form.vicidial_enabled ? M.enabled : M.disabled)}
                                    </Badge>
                                </div>
                            </div>
                            <div className="row g-3 mt-2">
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">{LABELS.baseUrl}</Form.Label>
                                        <Form.Control
                                            type="url"
                                            placeholder="https://vici.policytools.com"
                                            value={form.vicidial_base_url || ''}
                                            onChange={(e) => handleChange('vicidial_base_url', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">{LABELS.webphoneUrl}</Form.Label>
                                        <Form.Control
                                            type="url"
                                            placeholder="https://phone.viciphone.com/v3.0/viciphone.php"
                                            value={form.vicidial_webphone_url || ''}
                                            onChange={(e) => handleChange('vicidial_webphone_url', e.target.value)}
                                        />
                                        <Form.Text className="text-muted">
                                            {intl.formatMessage(M.webphoneUrlHint)}
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">{intl.formatMessage(M.apiUser)}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="6666"
                                            value={form.vicidial_api_user || ''}
                                            onChange={(e) => handleChange('vicidial_api_user', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">{intl.formatMessage(M.apiPassword)}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            autoComplete="off"
                                            placeholder="Enter API password"
                                            value={form.vicidial_api_password || ''}
                                            onChange={(e) => handleChange('vicidial_api_password', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">{intl.formatMessage(M.defaultAgentUser)}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="support01"
                                            value={form.vicidial_agent_user || ''}
                                            onChange={(e) => handleChange('vicidial_agent_user', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">{intl.formatMessage(M.defaultPhoneCode)}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="1"
                                            value={form.vicidial_phone_code || ''}
                                            onChange={(e) => handleChange('vicidial_phone_code', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <Form.Label className="fw-semibold text-gray-700 mb-0">{intl.formatMessage(TM.campaign)}</Form.Label>
                                            <Button
                                                variant="light-primary"
                                                size="sm"
                                                className="py-0 px-2 fs-8"
                                                onClick={handleLoadCampaigns}
                                                disabled={loadingCampaigns}
                                            >
                                                {loadingCampaigns ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : visibleCampaignOptions.length > 0 ? (
                                                    intl.formatMessage(M.refresh)
                                                ) : (
                                                    intl.formatMessage(M.campaignLoad)
                                                )}
                                            </Button>
                                        </div>
                                        {visibleCampaignOptions.length > 0 ? (
                                            <Form.Select
                                                value={form.vicidial_campaign_id || ''}
                                                onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                            >
                                                <option value=''>{intl.formatMessage(M.campaignSelectDefault)}</option>
                                                {visibleCampaignOptions.map((campaign) => (
                                                    <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                                        {campaignOptionLabel(intl, campaign)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                placeholder="0001"
                                                value={form.vicidial_campaign_id || ''}
                                                onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                            />
                                        )}
                                        <Form.Text className="text-muted d-block mt-1">
                                            <FormattedMessage {...M.campaignHint} values={{code: (chunks) => <code>{chunks}</code>}} />
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">{intl.formatMessage(M.dialDelay)}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            step={1}
                                            placeholder="5"
                                            value={form.vicidial_dial_queue_delay_seconds ?? 5}
                                            onChange={(e) =>
                                                handleChange(
                                                    'vicidial_dial_queue_delay_seconds',
                                                    e.target.value === '' ? 5 : Math.max(0, Number(e.target.value))
                                                )
                                            }
                                        />
                                        <Form.Text className="text-muted">
                                            {intl.formatMessage(M.dialDelayHint)}
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
            )}

            {/* ═════ MESSAGING & CHANNELS ═════ */}
            {visible.messaging && (
            <>
            <div className="d-flex align-items-center justify-content-between mb-4 mt-6">
                <div className="d-flex align-items-center gap-3">
                    <div className="bg-light-info rounded p-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                        <i className="bi bi-chat-left-text fs-2 text-info"></i>
                    </div>
                    <div>
                        <h4 className="fw-bold text-gray-900 mb-0">{intl.formatMessage(M.sectionMessaging)}</h4>
                        <span className="text-muted fs-8">{intl.formatMessage(M.messagingSubtitle)}</span>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                {/* ─── Twilio SMS Card ─── */}
                <div className="col-lg-6">
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Header className="bg-light border-0 py-3">
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-chat-dots fs-3 text-info"></i>
                                    <h5 className="fw-bold text-gray-800 mb-0">{LABELS.twilioSms}</h5>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Check
                                        type="switch"
                                        id="twilio_enabled"
                                        checked={form.twilio_enabled || false}
                                        onChange={(e) => handleChange('twilio_enabled', e.target.checked)}
                                        className="mb-0 ms-2"
                                    />
                                    <Badge bg={form.twilio_enabled ? 'success' : 'secondary'} className="px-3 py-2">
                                        {intl.formatMessage(form.twilio_enabled ? M.enabled : M.disabled)}
                                    </Badge>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {form.twilio_enabled ? (
                                <div className="d-flex flex-column gap-3">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{LABELS.accountSid}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="AC..."
                                                    value={form.twilio_account_sid || ''}
                                                    onChange={(e) => handleChange('twilio_account_sid', e.target.value)}
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{LABELS.authToken}</Form.Label>
                                                <div className="input-group input-group-sm">
                                                    <Form.Control
                                                        type={showTwilioToken ? 'text' : 'password'}
                                                        autoComplete="off"
                                                        placeholder="Token"
                                                        value={form.twilio_auth_token || ''}
                                                        onChange={(e) => handleChange('twilio_auth_token', e.target.value)}
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setShowTwilioToken(!showTwilioToken)}
                                                        title={showTwilioToken ? 'Hide' : 'Show'}
                                                    >
                                                        <i className={`bi ${showTwilioToken ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                    </Button>
                                                </div>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-9">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{intl.formatMessage(M.colPhoneNumber)}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="+1858..."
                                                    value={form.twilio_phone_number || ''}
                                                    onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-3 d-flex align-items-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleValidateTwilioSms}
                                                disabled={validatingTwilioSms}
                                                className="w-100"
                                            >
                                                {validatingTwilioSms ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    <>
                                                        <i className="bi bi-beaker me-1"></i> {intl.formatMessage(TM.test)}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                        <div className="col-12">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">
                                                    {LABELS.elasticTrunkSid}<span className="text-muted fw-normal ms-1">{LABELS.twilioVoiceTag}</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="TK..."
                                                    value={form.twilio_elastic_trunk_sid || ''}
                                                    onChange={(e) => handleChange('twilio_elastic_trunk_sid', e.target.value)}
                                                    size="sm"
                                                />
                                                <Form.Text className="text-muted fs-9">
                                                    {intl.formatMessage(M.elasticTrunkHint)}
                                                </Form.Text>
                                            </Form.Group>
                                        </div>
                                    </div>

                                    {/* Webhook URLs */}
                                    {config?.webhook_token && (
                                        <div className="mt-3 pt-3 border-top d-flex flex-column gap-2">
                                            <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light-primary border border-primary border-opacity-25">
                                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                    <Badge bg="primary" className="text-white fs-9 px-2">{LABELS.tagInbound}</Badge>
                                                    <code className="text-dark fs-8 text-truncate">{WEBHOOK_URL.twilioSms(config.webhook_token)}</code>
                                                </div>
                                                <Button
                                                    variant="light-primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/sms/${config.webhook_token}/`)
                                                        toast.success(intl.formatMessage(M.toastInboundWebhookCopied))
                                                    }}
                                                    className="p-1 px-2 border-0"
                                                    title="Copy Inbound Webhook"
                                                >
                                                    <i className="bi bi-clipboard"></i>
                                                </Button>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light-warning border border-warning border-opacity-25">
                                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                    <Badge bg="warning" className="text-dark fs-9 px-2">{LABELS.tagStatus}</Badge>
                                                    <code className="text-dark fs-8 text-truncate">{WEBHOOK_URL.twilioStatus()}</code>
                                                </div>
                                                <Button
                                                    variant="light-warning"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/status/`)
                                                        toast.success(intl.formatMessage(M.toastStatusCallbackCopied))
                                                    }}
                                                    className="p-1 px-2 border-0"
                                                    title="Copy Status Callback"
                                                >
                                                    <i className="bi bi-clipboard"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-chat-left-dots fs-1 d-block mb-2 text-gray-400"></i>
                                    <span className="fs-7">{intl.formatMessage(M.twilioSmsDisabled)}</span>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* ─── WhatsApp Integration Card ─── */}
                <div className="col-lg-6">
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Header className="bg-light border-0 py-3">
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-whatsapp fs-3 text-success"></i>
                                    <h5 className="fw-bold text-gray-800 mb-0">{LABELS.whatsapp}</h5>
                                    <Form.Check
                                        type="switch"
                                        id="whatsapp_enabled_switch"
                                        checked={form.whatsapp_provider !== 'NONE'}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const targetProvider = (form.whatsapp_provider && form.whatsapp_provider !== 'NONE') ? form.whatsapp_provider : 'TWILIO'
                                                handleChange('whatsapp_provider', targetProvider)
                                            } else {
                                                handleChange('whatsapp_provider', 'NONE')
                                            }
                                        }}
                                        className="mb-0 ms-2"
                                    />
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {form.whatsapp_provider !== 'NONE' && (
                                        <Form.Select
                                            value={form.whatsapp_provider || 'TWILIO'}
                                            onChange={(e) => handleChange('whatsapp_provider', e.target.value)}
                                            size="sm"
                                            className="w-auto"
                                        >
                                            {WHATSAPP_PROVIDER_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}
                                    <Badge bg={form.whatsapp_provider !== 'NONE' ? 'success' : 'secondary'} className="px-3 py-2">
                                        {form.whatsapp_provider !== 'NONE'
                                            ? form.whatsapp_provider
                                            : intl.formatMessage(M.disabled)}
                                    </Badge>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {form.whatsapp_provider === 'NONE' && (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-whatsapp fs-1 d-block mb-2 text-gray-400"></i>
                                    <span className="fs-7">{intl.formatMessage(M.whatsappPickProvider)}</span>
                                </div>
                            )}

                            {/* Botmaker WhatsApp Config */}
                            {form.whatsapp_provider === 'BOTMAKER' && (
                                <div className="d-flex flex-column gap-3 animate__animated animate__fadeIn">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{intl.formatMessage(M.botmakerApiToken)}</Form.Label>
                                                <div className="input-group input-group-sm">
                                                    <Form.Control
                                                        type={showBotmakerToken ? 'text' : 'password'}
                                                        autoComplete="off"
                                                        placeholder="JWT token"
                                                        value={form.botmaker_api_token || ''}
                                                        onChange={(e) => handleChange('botmaker_api_token', e.target.value)}
                                                        style={{ fontFamily: 'monospace' }}
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setShowBotmakerToken(!showBotmakerToken)}
                                                        title={showBotmakerToken ? 'Hide' : 'Show'}
                                                    >
                                                        <i className={`bi ${showBotmakerToken ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                    </Button>
                                                </div>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-5">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{intl.formatMessage(M.channelId)}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Channel ID"
                                                    value={form.botmaker_default_channel_id || ''}
                                                    onChange={(e) => handleChange('botmaker_default_channel_id', e.target.value)}
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-5">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{intl.formatMessage(M.phoneLine)}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="18586096799"
                                                    value={form.botmaker_phone_line_number || ''}
                                                    onChange={(e) => handleChange('botmaker_phone_line_number', e.target.value)}
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-2 d-flex align-items-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleValidateWhatsapp}
                                                disabled={validatingWhatsapp}
                                                className="w-100"
                                            >
                                                {validatingWhatsapp ? <Spinner animation="border" size="sm" /> : <i className="bi bi-beaker"></i>}
                                            </Button>
                                        </div>
                                    </div>
                                    {config?.webhook_token && (
                                        <div className="mt-3 pt-3 border-top">
                                            <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light-success border border-success border-opacity-25">
                                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                    <Badge bg="success" className="fs-9 px-2">{LABELS.tagBotmaker}</Badge>
                                                    <code className="text-dark fs-8 text-truncate">{WEBHOOK_URL.botmaker(config.webhook_token)}</code>
                                                </div>
                                                <Button
                                                    variant="light-success"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`https://app.policytools.com/webhooks/botmaker/${config.webhook_token}/`)
                                                        toast.success(intl.formatMessage(M.toastBotmakerWebhookCopied))
                                                    }}
                                                    className="p-1 px-2 border-0"
                                                    title="Copy Webhook URL"
                                                >
                                                    <i className="bi bi-clipboard"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Twilio WhatsApp Config */}
                            {form.whatsapp_provider === 'TWILIO' && (
                                <div className="d-flex flex-column gap-3 animate__animated animate__fadeIn">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{LABELS.accountSid}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={form.twilio_account_sid || 'AC...'}
                                                    value={form.twilio_whatsapp_account_sid || ''}
                                                    onChange={(e) => handleChange('twilio_whatsapp_account_sid', e.target.value)}
                                                    size="sm"
                                                />
                                                <Form.Text className="text-muted fs-9 d-block mt-1">
                                                    {intl.formatMessage(M.reuseSmsAccount)}
                                                </Form.Text>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{LABELS.authToken}</Form.Label>
                                                <div className="input-group input-group-sm">
                                                    <Form.Control
                                                        type={showTwilioWaToken ? 'text' : 'password'}
                                                        autoComplete="off"
                                                        placeholder={form.twilio_auth_token ? '••••••••' : 'Token'}
                                                        value={form.twilio_whatsapp_auth_token || ''}
                                                        onChange={(e) => handleChange('twilio_whatsapp_auth_token', e.target.value)}
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setShowTwilioWaToken(!showTwilioWaToken)}
                                                        title={showTwilioWaToken ? 'Hide' : 'Show'}
                                                    >
                                                        <i className={`bi ${showTwilioWaToken ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                    </Button>
                                                </div>
                                                <Form.Text className="text-muted fs-9 d-block mt-1">
                                                    {intl.formatMessage(M.reuseSmsAccount)}
                                                </Form.Text>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-9">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{intl.formatMessage(M.whatsappNumber)}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="+1858..."
                                                    value={form.twilio_whatsapp_number || ''}
                                                    onChange={(e) => handleChange('twilio_whatsapp_number', e.target.value)}
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-3 d-flex align-items-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleValidateWhatsapp}
                                                disabled={validatingWhatsapp}
                                                className="w-100"
                                            >
                                                {validatingWhatsapp ? <Spinner animation="border" size="sm" /> : <i className="bi bi-beaker"></i>}
                                            </Button>
                                        </div>
                                    </div>
                                    {config?.webhook_token && (
                                        <div className="mt-3 pt-3 border-top d-flex flex-column gap-2">
                                            <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light-primary border border-primary border-opacity-25">
                                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                    <Badge bg="primary" className="fs-9 px-2">{LABELS.tagInbound}</Badge>
                                                    <code className="text-dark fs-8 text-truncate">{WEBHOOK_URL.twilioWhatsapp(config.webhook_token)}</code>
                                                </div>
                                                <Button
                                                    variant="light-primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/whatsapp/${config.webhook_token}/`)
                                                        toast.success(intl.formatMessage(M.toastWhatsAppInboundCopied))
                                                    }}
                                                    className="p-1 px-2 border-0"
                                                >
                                                    <i className="bi bi-clipboard"></i>
                                                </Button>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light-warning border border-warning border-opacity-25">
                                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                    <Badge bg="warning" className="text-dark fs-9 px-2">{LABELS.tagStatus}</Badge>
                                                    <code className="text-dark fs-8 text-truncate">{WEBHOOK_URL.twilioStatus()}</code>
                                                </div>
                                                <Button
                                                    variant="light-warning"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/status/`)
                                                        toast.success(intl.formatMessage(M.toastStatusCallbackCopied))
                                                    }}
                                                    className="p-1 px-2 border-0"
                                                >
                                                    <i className="bi bi-clipboard"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Meta WhatsApp Config */}
                            {form.whatsapp_provider === 'META' && (
                                <div className="d-flex flex-column gap-3 animate__animated animate__fadeIn">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{LABELS.accessToken}</Form.Label>
                                                <div className="input-group input-group-sm">
                                                    <Form.Control
                                                        type={showMetaToken ? 'text' : 'password'}
                                                        placeholder="EAAL..."
                                                        value={form.meta_whatsapp_access_token || ''}
                                                        onChange={(e) => handleChange('meta_whatsapp_access_token', e.target.value)}
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setShowMetaToken(!showMetaToken)}
                                                        title={showMetaToken ? 'Hide' : 'Show'}
                                                    >
                                                        <i className={`bi ${showMetaToken ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                    </Button>
                                                </div>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-5">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{intl.formatMessage(M.phoneId)}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="ID"
                                                    value={form.meta_whatsapp_phone_number_id || ''}
                                                    onChange={(e) => handleChange('meta_whatsapp_phone_number_id', e.target.value)}
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-5">
                                            <Form.Group>
                                                <Form.Label className="fw-semibold text-gray-700 fs-8 mb-1">{LABELS.verifyToken}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Token"
                                                    value={form.meta_whatsapp_verify_token || ''}
                                                    onChange={(e) => handleChange('meta_whatsapp_verify_token', e.target.value)}
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-2 d-flex align-items-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleValidateWhatsapp}
                                                disabled={validatingWhatsapp}
                                                className="w-100"
                                            >
                                                {validatingWhatsapp ? <Spinner animation="border" size="sm" /> : <i className="bi bi-beaker"></i>}
                                            </Button>
                                        </div>
                                    </div>
                                    {config?.webhook_token && (
                                        <div className="mt-3 pt-3 border-top d-flex flex-column gap-2">
                                            <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light-primary border border-primary border-opacity-25">
                                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                    <Badge bg="primary" className="fs-9 px-2">{LABELS.tagMetaWebhook}</Badge>
                                                    <code className="text-dark fs-8 text-truncate">{WEBHOOK_URL.meta(config.webhook_token)}</code>
                                                </div>
                                                <Button
                                                    variant="light-primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/meta/${config.webhook_token}/`)
                                                        toast.success(intl.formatMessage(M.toastMetaWebhookCopied))
                                                    }}
                                                    className="p-1 px-2 border-0"
                                                >
                                                    <i className="bi bi-clipboard"></i>
                                                </Button>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light-secondary border">
                                                <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                                    <Badge bg="secondary" className="fs-9 px-2 text-dark">{LABELS.tagVerifyToken}</Badge>
                                                    <code className="text-dark fs-8 text-truncate">{form.meta_whatsapp_verify_token || EM_DASH}</code>
                                                </div>
                                                <Button
                                                    variant="light-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(form.meta_whatsapp_verify_token || '')
                                                        toast.success(intl.formatMessage(M.toastVerifyTokenCopied))
                                                    }}
                                                    disabled={!form.meta_whatsapp_verify_token}
                                                    className="p-1 px-2 border-0"
                                                >
                                                    <i className="bi bi-clipboard"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* ─── Conversational AI (part of Messaging & Channels — needs a channel to auto-reply on) ─── */}
                <div className={form.dify_enabled ? 'col-lg-6' : 'col-12'}>
                    <DifySection
                        difyEnabled={form.dify_enabled || false}
                        difyApiKey={form.dify_api_key || ''}
                        difyApiUrl={form.dify_api_url || 'https://api.dify.ai/v1'}
                        difyAppId={form.dify_app_id || ''}
                        onDifyEnabledChange={(value) => handleChange('dify_enabled', value)}
                        onDifyApiKeyChange={(value) => handleChange('dify_api_key', value)}
                        onDifyApiUrlChange={(value) => handleChange('dify_api_url', value)}
                        onDifyAppIdChange={(value) => handleChange('dify_app_id', value)}
                        organizationId={organizationId || ''}
                        aiProvider={form.ai_provider || 'DIFY'}
                        onAiProviderChange={(value) => handleChange('ai_provider', value)}
                        llmProvider={form.llm_provider || 'ANTHROPIC'}
                        onLlmProviderChange={(value) => handleChange('llm_provider', value)}
                        llmApiKey={form.llm_api_key || ''}
                        onLlmApiKeyChange={(value) => handleChange('llm_api_key', value)}
                        llmModel={form.llm_model || ''}
                        onLlmModelChange={(value) => handleChange('llm_model', value)}
                        disabledReason={
                            !hasMessagingChannel
                                ? 'Enable a messaging channel (Twilio SMS or WhatsApp) above to use Conversational AI.'
                                : undefined
                        }
                    />
                </div>

                {/* ─── Prompt de IA Conversacional (solo si IA Conversacional está activa) ─── */}
                {form.dify_enabled && (
                    <div className="col-lg-6">
                        <PromptConfigSection
                            isOrgScope={true}
                            promptHeader={form.prompt_header || ''}
                            onPromptHeaderChange={(value) => handleChange('prompt_header', value)}
                            promptBody={form.prompt_body || ''}
                            onPromptBodyChange={(value) => handleChange('prompt_body', value)}
                            promptFooter={form.prompt_footer || ''}
                            onPromptFooterChange={(value) => handleChange('prompt_footer', value)}
                            shareContactInfo={form.share_contact_info ?? null}
                            onShareContactInfoChange={(value) => handleChange('share_contact_info', value)}
                            shareQuotes={form.share_quotes ?? null}
                            onShareQuotesChange={(value) => handleChange('share_quotes', value)}
                            sharePolicies={form.share_policies ?? null}
                            onSharePoliciesChange={(value) => handleChange('share_policies', value)}
                            shareLeads={form.share_leads ?? null}
                            onShareLeadsChange={(value) => handleChange('share_leads', value)}
                            shareConsentStatus={form.share_consent_status ?? null}
                            onShareConsentStatusChange={(value) => handleChange('share_consent_status', value)}
                        />
                    </div>
                )}
            </div>
            </>
            )}

            {/* ═════ AI ASSISTANT & EMAIL SYSTEM ═════ */}
            {(visible.aiAssistant || visible.email) && (
            <div className="row g-4 mb-4">
                {/* ─── AI Assistant (internal chat widget) ─── */}
                {visible.aiAssistant && (
                <div className={visible.email ? 'col-lg-6' : 'col-12'}>
                    <AiAssistantSection
                        aiAssistantEnabled={form.ai_assistant_enabled ?? null}
                        onAiAssistantEnabledChange={(value) => handleChange('ai_assistant_enabled', value)}
                        aiAssistantDomains={form.ai_assistant_domains || {}}
                        onAiAssistantDomainsChange={(value) => handleChange('ai_assistant_domains', value)}
                    />
                </div>
                )}

                {/* ─── Email System ─── */}
                {visible.email && (
                <div className={visible.aiAssistant ? 'col-lg-6' : 'col-12'}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Header className="bg-light border-0 py-3">
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-envelope fs-3 text-warning"></i>
                                    <h5 className="fw-bold text-gray-800 mb-0">{intl.formatMessage(M.sectionEmail)}</h5>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Check
                                        type="switch"
                                        id="email_enabled"
                                        checked={form.email_enabled || false}
                                        onChange={(e) => handleChange('email_enabled', e.target.checked)}
                                        className="mb-0 ms-2"
                                    />
                                    <Badge bg={form.email_enabled ? 'success' : 'secondary'}>
                                        {intl.formatMessage(form.email_enabled ? M.enabled : M.disabled)}
                                    </Badge>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                        {form.email_enabled && (
                            <>
                                <div className="row g-1 mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{intl.formatMessage(M.smtpHost)}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="smtp.gmail.com"
                                                value={form.email_host || ''}
                                                onChange={(e) => handleChange('email_host', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{intl.formatMessage(M.port)}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="587"
                                                value={form.email_port || 587}
                                                onChange={(e) => handleChange('email_port', parseInt(e.target.value) || 587)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group className="mb-0 d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                id="email_use_tls"
                                                label="TLS"
                                                checked={form.email_use_tls !== false}
                                                onChange={(e) => handleChange('email_use_tls', e.target.checked)}
                                                className="mb-0 fs-7"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{intl.formatMessage(M.user)}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="user@example.com"
                                                value={form.email_host_user || ''}
                                                onChange={(e) => handleChange('email_host_user', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="row g-1 mt-1 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{intl.formatMessage(M.password)}</Form.Label>
                                            <Form.Control
                                                type="password"
                                                autoComplete="off"
                                                placeholder="••••••••"
                                                value={form.email_host_password || ''}
                                                onChange={(e) => handleChange('email_host_password', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{intl.formatMessage(M.from)}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="noti@example.com"
                                                value={form.email_from || ''}
                                                onChange={(e) => handleChange('email_from', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{LABELS.replyTo}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="support@example.com"
                                                value={form.email_reply_to || ''}
                                                onChange={(e) => handleChange('email_reply_to', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            </>
                        )}
                        </Card.Body>
                    </Card>
                </div>
                )}
            </div>
            )}

            {/* ═════ INTEGRATIONS ═════ */}
            {visible.integrations && (
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-link-45deg fs-2 text-success"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">{intl.formatMessage(M.sectionIntegrations)}</h4>
                    </div>
                    {/* ─── HealthSherpa Medicare ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-heart-pulse fs-4 text-danger"></i>
                            <h5 className="fw-bold text-gray-800 mb-0">{LABELS.healthSherpa}</h5>
                            <Form.Check
                                type="switch"
                                id="healthsherpa_enabled"
                                checked={form.healthsherpa_enabled || false}
                                onChange={(e) => handleChange('healthsherpa_enabled', e.target.checked)}
                                className="ms-2 mb-0"
                            />
                            <Badge bg={form.healthsherpa_enabled ? 'success' : 'secondary'} className="ms-2">
                                {intl.formatMessage(form.healthsherpa_enabled ? M.enabled : M.disabled)}
                            </Badge>
                        </div>

                        {form.healthsherpa_enabled && (
                            <div className="row g-1 mt-2 align-items-end">
                                <div className="col-md-4">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{intl.formatMessage(M.agentEmail)}</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder="agent@example.com"
                                            value={form.healthsherpa_email || ''}
                                            onChange={(e) => handleChange('healthsherpa_email', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{LABELS.apiKey}</Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="off"
                                            placeholder="Key"
                                            value={form.healthsherpa_api_key || ''}
                                            onChange={(e) => handleChange('healthsherpa_api_key', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-5">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">{intl.formatMessage(M.webhookSecret)}</Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="off"
                                            placeholder="Secret"
                                            value={form.healthsherpa_webhook_secret || ''}
                                            onChange={(e) => handleChange('healthsherpa_webhook_secret', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>
            )}

            {/* Channel Form Modal */}
            {organizationId && (
                <ChannelFormModal
                    isOpen={channelModalOpen}
                    onClose={() => {
                        setChannelModalOpen(false)
                        setEditingChannel(null)
                    }}
                    onSave={handleSaveChannel}
                    saving={channelsSaving}
                    initialData={editingChannel}
                    groupMembers={[]} // Empty for org-level channels
                />
            )}
        </div>
    )
}
